import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { GraphemeMnemonicsPronunciationScreen } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._screens.pronunciation";
import { type ReactNode } from "react";

/**
 * The per-reading pronunciation sections for the Reference page, replacing the mnemonics panel's
 * per-pinyin tabs.
 *
 * The reading list is derived exactly as `MorphemeMnemonicsViewSuspense` derives it — the panel
 * slice's `pinyinList` narrowed by `usePrevalentPinyin`, then gated on `isPinyinMnemonicsEnabled`.
 * With the setting off the panel drops its pronunciation tabs entirely and surfaces the reading
 * inline under the glyph instead, so this yields no sections rather than empty ones.
 *
 * The slice comes from `GraphemeMorphemePanelsContext`, narrowed by the morpheme page layout that
 * mounted the Reference page — never re-read from the (nullable) slice query.
 *
 * Returns a descriptor list rather than rendered rows so the Reference page supplies its own
 * section chrome, matching how the info screens are exposed.
 */
export function useGraphemeMnemonicsPronunciationReferenceSectionList(grapheme: string): {
  render: () => ReactNode;
  title: string;
  toneNumbersPinyin: string;
}[] {
  const [pinyinList] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.pinyinList,
  );
  const prevalentPinyinList = usePrevalentPinyin(grapheme, pinyinList);
  const { isPinyinMnemonicsEnabled } = useActorUser();

  if (!isPinyinMnemonicsEnabled) {
    return [];
  }

  return prevalentPinyinList.map((pinyin) => ({
    render: () => (
      <GraphemeMnemonicsPronunciationScreen
        rawGrapheme={grapheme}
        rawPinyin={pinyin.toneNumbersPinyin}
      />
    ),
    title: `Pronunciation · ${toDiacriticPinyin(pinyin.toneNumbersPinyin)}`,
    toneNumbersPinyin: pinyin.toneNumbersPinyin,
  }));
}
